import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from 'flowbite-react';
import { format } from 'date-fns';

// Hooks
import { usePageTitle } from '../hooks/usePageTitle';
import { useAppSelector } from '../redux/hooks/useAppSelector';

// Components
import { WeeklyLogAnalyticsFilters } from '../components/charts/WeeklyLogAnalyticsFilters';
import { WeeklyLogAnalyticsDashboard } from '../components/charts/WeeklyLogAnalyticsDashboard';
import { Loading } from '../components/Shared/Loading/Loading';
import { ErrorComponent } from '../components/Shared/ErrorComponent/ErrorComponent';

// API hooks
import { useFetchUsersWeekLogAnalyticsQuery } from '../redux/api/statisticsApi';

// Selectors
import { selectIsDarkMode } from '../redux/selectors/displaySettingsSelectors';

const WeekLogAnalyticsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isDarkMode = useAppSelector(selectIsDarkMode);

  usePageTitle(t('analytics.title'));

  const buttonClassName = `btn ${isDarkMode ? 'btn-dark' : 'btn-light'}`;

  const handleBackToDashboard = () => {
    navigate('/');
  };

  // Initialize with last 30 days as default date range
  const getDefaultDateRange = () => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 30);
    return { startDate, endDate };
  };

  const [filters, setFilters] = useState({
    questionnaireId: '',
    questionId: '',
    answerIds: [] as string[],
    ...getDefaultDateRange(),
  });

  // Format dates to ISO string for API
  const formatDateForAPI = (date: Date) => {
    return format(date, 'yyyy-MM-dd');
  };

  const {
    data: analyticsData,
    isLoading,
    isFetching,
    error,
  } = useFetchUsersWeekLogAnalyticsQuery({
    questionnaireId: filters.questionnaireId || undefined,
    questionId: filters.questionId || undefined,
    answerIds: filters.answerIds.length > 0 ? filters.answerIds : undefined,
    startDate: formatDateForAPI(filters.startDate),
    endDate: formatDateForAPI(filters.endDate),
  });

  const isLoadingData = isLoading || isFetching;

  return (
    <div className="space-y-6 p-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t('analytics.title')}
          </h1>
          <p className="mt-1 text-sm text-black dark:text-primary-25">
            {t('analytics.subtitle')}
          </p>
        </div>
        <Button
          onClick={handleBackToDashboard}
          size="xs"
          className={`${buttonClassName}`}
        >
          {t('common.buttons.backToDashboard')}
        </Button>
      </div>

      {/* Filters */}
      <WeeklyLogAnalyticsFilters
        filters={filters}
        onFiltersChange={setFilters}
      />

      {/* Analytics Dashboard */}
      <div className="rounded-2xl bg-white p-4 shadow">
        {isLoadingData && (
          <div className="flex h-64 items-center justify-center">
            <Loading />
          </div>
        )}

        {error && !isLoadingData && (
          <div className="flex h-64 items-center justify-center">
            <ErrorComponent errorMessage={t('common.error.loadingData')} />
          </div>
        )}

        {!isLoadingData && !error && analyticsData && (
          <WeeklyLogAnalyticsDashboard
            analyticsData={analyticsData.data}
            isLoading={isLoadingData}
            error={error}
          />
        )}

        {!isLoadingData && !error && !analyticsData && (
          <div className="flex h-64 items-center justify-center">
            <p className="text-gray-500 dark:text-gray-400">
              {t('analytics.noData')}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default WeekLogAnalyticsPage;
